import React, { useMemo, useState, useEffect } from "react";

import { formatTime } from "../../utils/formatTime";
import { formatDateToDDMMYY } from "../../utils/formatDate";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// -------------------------
// Helpers
// -------------------------
function isUpcomingMonth(year, month) {
  const now = new Date();
  const cy = now.getFullYear();
  const cm = now.getMonth() + 1;

  if (year !== cy) return false;
  return month > cm;
}

function parseEntryDateParts(entry) {
  const raw = entry?.date;
  if (!raw || raw === "Unknown") return null;

  const str = String(raw).trim();
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (iso) {
    const year = parseInt(iso[1], 10);
    const month = parseInt(iso[2], 10);
    const day = parseInt(iso[3], 10) || 1;

    if (year && month >= 1 && month <= 12) {
      return {
        year,
        month,
        ts: new Date(year, month - 1, day).getTime(),
        rawDate: str,
      };
    }
  }

  return null;
}

function analyzeHistory(history) {
  const yearsSet = new Set();
  const ymHasData = new Set();

  let latest = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  };

  let maxTs = -Infinity;

  for (const entry of history) {
    const p = parseEntryDateParts(entry);
    if (!p) continue;

    yearsSet.add(p.year);
    ymHasData.add(`${p.year}-${String(p.month).padStart(2, "0")}`);

    if (p.ts >= maxTs) {
      maxTs = p.ts;
      latest = { year: p.year, month: p.month };
    }
  }

  const years = Array.from(yearsSet).sort((a, b) => b - a);

  if (years.length === 0) {
    const y = new Date().getFullYear();
    years.push(y);
    latest = { year: y, month: new Date().getMonth() + 1 };
  }

  return { years, ymHasData, latest };
}

function groupHistoryByDate(entries) {
  const map = new Map();

  for (const entry of entries) {
    const dateKey = entry.date || "Unknown";

    if (!map.has(dateKey)) {
      map.set(dateKey, []);
    }

    map.get(dateKey).push(entry);
  }

  const rows = Array.from(map.entries());
  rows.sort(([a], [b]) => String(a).localeCompare(String(b)));

  return rows;
}

// -------------------------
// Component
// -------------------------
export default function CounterHistory({ history = [] }) {
  const { years, ymHasData, latest } = useMemo(
    () => analyzeHistory(history),
    [history]
  );

  const [selectedYear, setSelectedYear] = useState(latest.year);
  const [selectedMonth, setSelectedMonth] = useState(latest.month);
  const [viewMode, setViewMode] = useState("month");

  // Sync latest
  useEffect(() => {
    setSelectedYear(latest.year);
    setSelectedMonth(latest.month);
  }, [latest.year, latest.month, history]);

  // Prevent future month selection
  useEffect(() => {
    if (viewMode !== "month") return;

    if (isUpcomingMonth(selectedYear, selectedMonth)) {
      const cm = new Date().getMonth() + 1;
      setSelectedMonth(cm);
    }
  }, [viewMode, selectedYear, selectedMonth]);

  // -------------------------
  // Filtering
  // -------------------------
  const filteredHistory = useMemo(() => {
    return history.filter((entry) => {
      const p = parseEntryDateParts(entry);

      if (!p || p.year !== selectedYear) return false;
      if (viewMode === "year") return true;

      return p.month === selectedMonth;
    });
  }, [history, selectedYear, selectedMonth, viewMode]);

  const groupedByDate = useMemo(
    () => groupHistoryByDate(filteredHistory),
    [filteredHistory]
  );

  // -------------------------
  // Totals
  // -------------------------
  const {
    totalCountInView,
    totalSpentSecondsInView,
    totalLabel,
  } = useMemo(() => {
    let countSum = 0;
    let timeSum = 0;

    for (const e of filteredHistory) {
      countSum += Number(e.count) || 0;
      timeSum += Number(e.spentTime) || 0;
    }

    const label =
      viewMode === "month"
        ? `Total (${MONTH_LABELS[selectedMonth - 1]} ${selectedYear})`
        : `Total (${selectedYear})`;

    return {
      totalCountInView: countSum,
      totalSpentSecondsInView: timeSum,
      totalLabel: label,
    };
  }, [filteredHistory, viewMode, selectedMonth, selectedYear]);

  // -------------------------
  // Empty State
  // -------------------------
  if (!history.length) {
    return (
      <div className="noRecord-found">
        <p>No history available</p>
      </div>
    );
  }

  const monthHasData = (m) =>
    ymHasData.has(
      `${selectedYear}-${String(m).padStart(2, "0")}`
    );

  const emptyMessage =
    viewMode === "year"
      ? `No entries in ${selectedYear}.`
      : `No entries in ${MONTH_LABELS[selectedMonth - 1]} ${selectedYear}.`;

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="counterHistory">
      {/* View Toggle */}
      <div
        className="counterHistory__viewToggle"
        role="group"
        aria-label="History view mode"
      >
        <button
          type="button"
          className={`counterHistory__viewBtn ${
            viewMode === "month" ? "is-active" : ""
          }`}
          aria-pressed={viewMode === "month"}
          onClick={() => setViewMode("month")}
        >
          By month
        </button>

        <button
          type="button"
          className={`counterHistory__viewBtn ${
            viewMode === "year" ? "is-active" : ""
          }`}
          aria-pressed={viewMode === "year"}
          onClick={() => setViewMode("year")}
        >
          Whole year
        </button>
      </div>

      {/* Year Tabs */}
      {years.length > 1 && (
        <div
          className="counterHistory__years"
          role="tablist"
        >
          {years.map((y) => (
            <button
              key={y}
              type="button"
              role="tab"
              aria-selected={selectedYear === y}
              className={`counterHistory__yearTab ${
                selectedYear === y ? "is-active" : ""
              }`}
              onClick={() => {
                setSelectedYear(y);

                const firstWithData =
                  MONTH_LABELS.findIndex((_, idx) =>
                    ymHasData.has(
                      `${y}-${String(idx + 1).padStart(2, "0")}`
                    )
                  );

                setSelectedMonth(
                  firstWithData >= 0 ? firstWithData + 1 : 1
                );
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Month Tabs */}
      {viewMode === "month" ? (
        <div
          className="counterHistory__months"
          role="tablist"
        >
          {MONTH_LABELS.map((label, idx) => {
            const m = idx + 1;
            const has = monthHasData(m);
            const active = selectedMonth === m;
            const upcoming = isUpcomingMonth(selectedYear, m);

            return (
              <button
                key={label}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={upcoming}
                className={`counterHistory__monthTab ${active ? "is-active" : ""} ${has ? "has-data" : ""} ${upcoming ? "is-disabled" : ""}`}
                onClick={() =>
                  !upcoming && setSelectedMonth(m)
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="counterHistory__yearHint">
          Showing all entries for {selectedYear}.
        </p>
      )}

      {/* Table / Empty */}
      {groupedByDate.length === 0 ? (
        <div className="counterHistory__empty">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="counterHistory__tableWrap">
          <table className="counterHistory__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Count</th>
                <th>Time taken</th>
              </tr>
            </thead>

            <tbody>
              {groupedByDate.map(([rawDate, entries]) =>
                entries.map((entry, idx) => (
                  <tr key={`${rawDate}-${idx}`}>
                    {idx === 0 && (
                      <td
                        rowSpan={entries.length}
                        className="counterHistory__dateCell"
                      >
                        {formatDateToDDMMYY(rawDate)}
                      </td>
                    )}

                    <td className="counterHistory__numCell">
                      {entry.count}
                    </td>

                    <td className="counterHistory__durationCell">
                      {formatTime(entry.spentTime || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr className="counterHistory__totalRow">
                <td>{totalLabel}</td>
                <td className="counterHistory__numCell">
                  {totalCountInView}
                </td>
                <td className="counterHistory__durationCell">
                  {formatTime(totalSpentSecondsInView)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}